/**
 * @fileoverview Confirmações das ferramentas de Super Admin.
 *
 * - "create" / "resume": confirmação forte — senha do próprio superadmin +
 *   frase exata da operação (a frase vem do servidor, que confere as duas de
 *   novo). O botão só libera com a frase conferindo; erros (senha, limite de
 *   tentativas, operação em andamento...) aparecem aqui dentro.
 * - "cancel": pedir o cancelamento da operação em andamento.
 *
 * Resolver não apaga nada: textos próprios e botão neutro (o vermelho fica
 * para a exclusão).
 *
 * A senha não sobrevive a um erro nem entre aberturas: do erro guarda só o
 * código (o objeto do axios traz o corpo da requisição, com a senha).
 */

import React, { useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { Loader2, ShieldAlert, X } from "lucide-react";

import useStyles from "./styles";
import { errorCodeOf, messageForCode, t } from "./format";

const ConfirmDialog = ({ request, onClose, onSubmit }) => {
	const classes = useStyles();
	const [password, setPassword] = useState("");
	const [typed, setTyped] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [errorCode, setErrorCode] = useState(null);
	const passwordRef = useRef(null);
	const mounted = useRef(true);
	// Enquanto o diálogo some (transição), segue mostrando o que confirmava.
	const lastRequest = useRef(null);
	if (request) lastRequest.current = request;
	const shown = request || lastRequest.current;

	useEffect(() => () => {
		mounted.current = false;
	}, []);

	useEffect(() => {
		setPassword("");
		setTyped("");
		setErrorCode(null);
		setSubmitting(false);
	}, [request]);

	const mode = shown?.mode || "create";
	const isResolve = shown?.operation === "resolve_tickets";
	const needsSecret = mode !== "cancel";
	const phrase = shown?.phrase || "";
	const phraseOk = phrase !== "" && typed.trim() === phrase;
	const canSubmit = !submitting && (!needsSecret || (phraseOk && password.length > 0));

	const handleClose = () => {
		if (!submitting) onClose();
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (!canSubmit) return;
		setSubmitting(true);
		setErrorCode(null);
		try {
			await onSubmit(needsSecret ? { password, confirmationPhrase: typed.trim() } : {});
		} catch (err) {
			if (!mounted.current) return;
			const code = errorCodeOf(err) || "UNKNOWN";
			setErrorCode(code);
			setPassword("");
			if (code === "ERR_INVALID_PASSWORD") setTimeout(() => passwordRef.current?.focus(), 0);
		} finally {
			if (mounted.current) setSubmitting(false);
		}
	};

	const titles = {
		create: t(isResolve ? "confirm.resolve.createTitle" : "confirm.createTitle"),
		resume: t("confirm.resumeTitle"),
		cancel: t(isResolve ? "progress.resolve.cancelTitle" : "progress.cancelTitle"),
	};
	const submitLabels = {
		create: t(isResolve ? "confirm.resolve.submit" : "confirm.submit"),
		resume: t("confirm.resumeSubmit"),
		cancel: t("progress.cancel"),
	};

	return (
		<Dialog
			open={!!request}
			onClose={handleClose}
			PaperProps={{ className: classes.confirmPaper, elevation: 0 }}
			BackdropProps={{ className: classes.backdrop }}
			aria-labelledby="admin-tools-confirm-title"
		>
			<form onSubmit={handleSubmit} autoComplete="off">
				<div className={classes.confirmHead}>
					<span
						className={`${classes.confirmIcon} ${isResolve ? classes.confirmIconNeutral : ""}`}
						aria-hidden="true"
					>
						<ShieldAlert size={22} strokeWidth={1.8} />
					</span>
					<div style={{ flex: 1, minWidth: 0 }}>
						<div className={classes.eyebrow}>{t(isResolve ? "confirm.resolve.eyebrow" : "confirm.eyebrow")}</div>
						<h2 id="admin-tools-confirm-title" className={classes.confirmTitle}>
							{titles[mode]}
						</h2>
					</div>
					<button
						type="button"
						className={classes.closeBtn}
						onClick={handleClose}
						disabled={submitting}
						aria-label={t("close")}
					>
						<X size={16} strokeWidth={2} />
					</button>
				</div>

				<div className={classes.confirmBody}>
					{mode === "cancel" ? (
						<div>{t(isResolve ? "progress.resolve.cancelMessage" : "progress.cancelMessage")}</div>
					) : (
						<>
							<div>
								{mode === "resume"
									? t("confirm.resumeIntro")
									: t(isResolve ? "confirm.resolve.intro" : "confirm.intro")}
							</div>
							<div className={classes.summaryBox}>
								<ul>
									{(shown?.summary || []).map((line, index) => (
										<li key={index}>{line}</li>
									))}
								</ul>
							</div>

							<label className={classes.fieldBlock}>
								<span className={classes.label}>{t("confirm.password")}</span>
								<input
									ref={passwordRef}
									type="password"
									autoComplete="current-password"
									className={classes.input}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									disabled={submitting}
									autoFocus
								/>
							</label>

							<label className={classes.fieldBlock}>
								<span className={classes.label}>
									{t("confirm.phraseBefore")} <span className={classes.phrase}>{phrase}</span>{" "}
									{t("confirm.phraseAfter")}
								</span>
								<input
									type="text"
									className={classes.input}
									value={typed}
									onChange={(e) => setTyped(e.target.value)}
									placeholder={phrase}
									autoComplete="off"
									spellCheck={false}
									disabled={submitting}
								/>
							</label>
						</>
					)}

					{errorCode && (
						<div className={classes.errorBox} role="alert">
							{messageForCode(errorCode)}
						</div>
					)}
				</div>

				<div className={classes.confirmActions}>
					<button type="button" className={classes.btnGhost} onClick={handleClose} disabled={submitting}>
						{mode === "cancel" ? t("progress.keepRunning") : t("confirm.cancel")}
					</button>
					<button
						type="submit"
						className={isResolve && mode !== "cancel" ? classes.btnInk : classes.btnDanger}
						disabled={!canSubmit}
					>
						{submitting && <Loader2 size={15} className={classes.spin} />}
						{submitLabels[mode]}
					</button>
				</div>
			</form>
		</Dialog>
	);
};

export default ConfirmDialog;
